/**
 * 品牌画像生成单例（模块级，跨页面存活）。
 *
 * 表单页调用 start() 打开 SSE 触发生成并拿到 portraitId；画像页直接读本单例的
 * 响应式状态做逐块渲染。浏览器刷新导致单例丢失时，画像页用 resume() 走
 * /stream/events 重放 + query 兜底恢复。
 */
import { reactive, computed } from 'vue'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { TOKEN_KEY, getToken } from '@/core/request/token'
import { normalizeErrorMessage } from '@/core/request/error'
import { streamEvents, insightQuery } from '../api/index'
import { moduleReady } from './portraitMapping'

const STREAM_URL = `${process.env.VUE_APP_BASE_API || ''}/brand-insight/portrait/generate/stream`
const INFLIGHT_KEY = 'geo_portrait_inflight'
const LAST_REQUEST_KEY = 'geo_portrait_last_request'
const REQUEST_CACHE_PREFIX = 'geo_portrait_request_'

type GenStatus = 'idle' | 'running' | 'done' | 'error'
type CachedRequestStatus = 'running' | 'error'

interface CachedRequest {
  payload: Record<string, any> | null
  status: CachedRequestStatus
  errorMsg: string
  errorMessageReplaced: boolean
}

interface GenState {
  portraitId: string
  executionId: string
  status: GenStatus
  raw: Record<string, any>
  progress: number
  message: string
  errorMsg: string
  errorMessageReplaced: boolean
  // 本次发起时选择的 AI 平台，同会话内供后续步骤（如权益点计算）读取
  platformKeys: string[]
}

const state = reactive<GenState>({
  portraitId: '',
  executionId: '',
  status: 'idle',
  raw: {},
  progress: 0,
  message: '',
  errorMsg: '',
  errorMessageReplaced: false,
  platformKeys: [],
})

let abortCtrl: AbortController | null = null
let resumeTimer: ReturnType<typeof setTimeout> | null = null
let lastRequestPayload: Record<string, any> | null = null

function clonePayload(payload: Record<string, any>): Record<string, any> {
  return JSON.parse(JSON.stringify(payload))
}

// 画像重试没有专属后端接口，需要把首次表单参数完整保留下来，重新调用生成接口。
function rememberRequest(payload: Record<string, any>) {
  lastRequestPayload = clonePayload(payload)
  try {
    sessionStorage.setItem(LAST_REQUEST_KEY, JSON.stringify(lastRequestPayload))
  } catch {
    /* ignore */
  }
}

function readLastRequest(): Record<string, any> | null {
  if (lastRequestPayload) return clonePayload(lastRequestPayload)
  try {
    const stored = sessionStorage.getItem(LAST_REQUEST_KEY)
    if (!stored) return null
    const parsed = JSON.parse(stored)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
    lastRequestPayload = parsed
    return clonePayload(parsed)
  } catch {
    return null
  }
}

function requestCacheKey(portraitId: string) {
  return `${REQUEST_CACHE_PREFIX}${portraitId}`
}

function cachePortraitRequest(
  portraitId: string,
  status: CachedRequestStatus,
  errorMsg = '',
  errorMessageReplaced = false,
) {
  if (!portraitId || !lastRequestPayload) return
  const cached: CachedRequest = {
    payload: clonePayload(lastRequestPayload),
    status,
    errorMsg,
    errorMessageReplaced,
  }
  try {
    sessionStorage.setItem(requestCacheKey(portraitId), JSON.stringify(cached))
  } catch {
    /* ignore */
  }
}

function readCachedRequest(portraitId: string): CachedRequest | null {
  try {
    const stored = sessionStorage.getItem(requestCacheKey(portraitId))
    if (!stored) return null
    const parsed = JSON.parse(stored)
    if (!parsed || (parsed.status !== 'running' && parsed.status !== 'error')) return null
    const payload =
      parsed.payload && typeof parsed.payload === 'object' && !Array.isArray(parsed.payload)
        ? parsed.payload
        : null
    const normalizedError =
      parsed.status === 'error'
        ? normalizeErrorMessage(parsed.errorMsg)
        : { message: '', replaced: false }
    return {
      payload,
      status: parsed.status,
      errorMsg: normalizedError.message,
      errorMessageReplaced: parsed.errorMessageReplaced === true || normalizedError.replaced,
    }
  } catch {
    return null
  }
}

function clearCachedRequest(portraitId: string) {
  try {
    sessionStorage.removeItem(requestCacheKey(portraitId))
  } catch {
    /* ignore */
  }
}

function restoreRequestPayload(portraitId: string): CachedRequest | null {
  const cached = readCachedRequest(portraitId)
  if (!cached?.payload) return cached
  lastRequestPayload = clonePayload(cached.payload)
  if (Array.isArray(cached.payload.platformKeys)) {
    state.platformKeys = [...cached.payload.platformKeys]
  }
  return cached
}

function getRetryPayload(portraitId?: string): Record<string, any> | null {
  if (portraitId) {
    const cached = readCachedRequest(portraitId)
    if (cached?.payload) return clonePayload(cached.payload)
    // 同一页面内存仍在、但浏览器缓存不可用时，继续允许本次会话重试。
    if (state.portraitId === String(portraitId) && lastRequestPayload) {
      return clonePayload(lastRequestPayload)
    }
    return null
  }
  return readLastRequest()
}

function hasRetryPayload(portraitId?: string): boolean {
  return !!getRetryPayload(portraitId)
}

function setInflight(portraitId: string, lastEventId: number) {
  try {
    sessionStorage.setItem(INFLIGHT_KEY, JSON.stringify({ portraitId, lastEventId }))
  } catch {
    /* ignore */
  }
}
function readInflight(): { portraitId: string; lastEventId: number } | null {
  try {
    const s = sessionStorage.getItem(INFLIGHT_KEY)
    return s ? JSON.parse(s) : null
  } catch {
    return null
  }
}
function clearInflight() {
  try {
    sessionStorage.removeItem(INFLIGHT_KEY)
  } catch {
    /* ignore */
  }
}

/** 刷新后从任务缓存恢复失败状态；路由已明确标记失败时可强制按失败态恢复。 */
function restoreFailed(portraitId: string, force = false): boolean {
  const cached = restoreRequestPayload(String(portraitId))
  if (!cached || (!force && cached.status !== 'error')) return false
  state.portraitId = String(portraitId)
  state.executionId = ''
  state.status = 'error'
  state.raw = {}
  state.progress = 0
  state.message = ''
  state.errorMsg = cached.errorMsg || '生成失败'
  state.errorMessageReplaced = cached.errorMessageReplaced
  clearInflight()
  return true
}

function reset() {
  abortCtrl?.abort()
  abortCtrl = null
  if (resumeTimer) {
    clearTimeout(resumeTimer)
    resumeTimer = null
  }
  state.portraitId = ''
  state.executionId = ''
  state.status = 'idle'
  state.raw = {}
  state.progress = 0
  state.message = ''
  state.errorMsg = ''
  state.errorMessageReplaced = false
  state.platformKeys = []
}

function markFailed(message?: unknown) {
  // 流式接口可能已经返回部分模块；只要最终失败，半成品就不能继续展示或保存。
  const normalized = normalizeErrorMessage(message)
  cachePortraitRequest(state.portraitId, 'error', normalized.message, normalized.replaced)
  state.executionId = ''
  state.raw = {}
  state.progress = 0
  state.message = ''
  state.errorMsg = normalized.message
  state.errorMessageReplaced = normalized.replaced
  state.status = 'error'
  clearInflight()
}

/** 应用一条事件到状态。返回是否为终止事件。 */
function applyEvent(eventName: string, data: any): boolean {
  if (data?.portraitId) {
    const eventPortraitId = String(data.portraitId)
    if (!state.portraitId) state.portraitId = eventPortraitId
    if (state.portraitId === eventPortraitId) {
      cachePortraitRequest(eventPortraitId, 'running')
    }
  }
  switch (eventName) {
    case 'progress':
      if (typeof data?.progress === 'number') state.progress = data.progress
      if (data?.message) state.message = data.message
      return false
    case 'module':
      if (data?.module) state.raw[data.module] = data.data
      if (typeof data?.progress === 'number') state.progress = data.progress
      return false
    case 'complete':
      if (data?.executionId) state.executionId = String(data.executionId)
      if (data?.portrait) state.raw = { ...state.raw, ...data.portrait }
      state.progress = 100
      state.status = 'done'
      clearInflight()
      clearCachedRequest(state.portraitId)
      return true
    case 'error':
      markFailed(data?.message)
      return true
    default:
      return false
  }
}

/**
 * 发起生成：打开 SSE，首个携带 portraitId 的事件即 resolve（供表单页跳转），
 * 连接保持到 complete/error。
 */
function start(payload: Record<string, any>): Promise<string> {
  const requestPayload = clonePayload(payload)
  reset()
  rememberRequest(requestPayload)
  state.status = 'running'
  if (Array.isArray(requestPayload?.platformKeys)) {
    state.platformKeys = [...requestPayload.platformKeys]
  }
  abortCtrl = new AbortController()
  let resolved = false

  return new Promise<string>((resolve, reject) => {
    fetchEventSource(STREAM_URL, {
      method: 'POST',
      openWhenHidden: true,
      signal: abortCtrl!.signal,
      headers: {
        'Content-Type': 'application/json',
        [TOKEN_KEY]: getToken() || '',
        'x-xsrf-token': getToken() || '',
        'geo-source-type': localStorage.getItem('geoMode') || 'inner',
      },
      body: JSON.stringify(requestPayload),
      onmessage(ev) {
        if (!ev.data) return
        let data: any
        try {
          data = JSON.parse(ev.data)
        } catch {
          return
        }
        const terminal = applyEvent(ev.event || 'message', data)
        if (state.portraitId && !terminal) setInflight(state.portraitId, 0)
        if (!resolved && state.portraitId) {
          resolved = true
          if (state.status === 'error') {
            reject(new Error(state.errorMsg || '画像生成失败'))
          } else {
            resolve(state.portraitId)
          }
        }
      },
      onerror(err) {
        // 抛出以阻止 fetchEventSource 无限重连
        markFailed(err instanceof Error ? err.message : '生成连接异常，请重试')
        if (!resolved) {
          reject(err)
        }
        throw err
      },
      onclose() {
        if (state.status === 'running') {
          const error = new Error('生成连接已中断，请重试')
          markFailed(error.message)
          if (!resolved) reject(error)
        }
      },
    }).catch(() => {
      /* onerror 已处理 */
    })
  })
}

/** 使用指定画像任务首次提交的完整表单参数，重新发起一条画像生成任务。 */
function retry(portraitId?: string): Promise<string> {
  const payload = getRetryPayload(portraitId)
  if (!payload) {
    return Promise.reject(new Error('缺少原始表单数据，请返回上一步重新提交'))
  }
  return start(payload)
}

/**
 * 刷新后恢复：先用 query 探测是否已落库完成；否则轮询 /stream/events 重放增量，
 * 并以 query 落库数据作为最终完成判定（不依赖事件缓冲是否完整）。
 */
async function resume(portraitId: string): Promise<void> {
  restoreRequestPayload(String(portraitId))
  state.portraitId = String(portraitId)
  if (state.status === 'idle') state.status = 'running'
  let after = readInflight()?.portraitId === String(portraitId) ? readInflight()!.lastEventId : 0

  const poll = async () => {
    // 1) 拉取增量事件重放
    try {
      const { data } = await streamEvents({ type: 'portrait', id: portraitId, after })
      const events: any[] = data?.events || []
      let terminal = false
      events.forEach((e: any) => {
        terminal = applyEvent(e.eventName, e.data) || terminal
        if (typeof e.eventId === 'number') after = e.eventId + 1
      })
      if (terminal) clearInflight()
      else setInflight(String(portraitId), after)
    } catch {
      /* ignore，转向 query 兜底 */
    }
    if (state.status === 'done' || state.status === 'error') return

    // 2) query 兜底：落库即视为完成（不依赖缓冲完整性）
    try {
      const { data } = await insightQuery({ portraitId })
      if (data && data.brand) {
        state.raw = { ...state.raw, ...data }
        if (data.executionId) state.executionId = String(data.executionId)
        state.status = 'done'
        clearInflight()
        clearCachedRequest(String(portraitId))
        return
      }
    } catch {
      /* 行尚未写入数据，继续轮询 */
    }
    resumeTimer = setTimeout(poll, 2000)
  }
  await poll()
}

const moduleReadyState = computed(() => moduleReady(state.raw))
const isGenerating = computed(() => state.status === 'running')
const isDone = computed(() => state.status === 'done')
const isError = computed(() => state.status === 'error')

export function usePortraitGen() {
  return {
    state,
    moduleReadyState,
    isGenerating,
    isDone,
    isError,
    start,
    retry,
    resume,
    reset,
    restoreFailed,
    hasRetryPayload,
    readInflight,
    clearInflight,
  }
}
